import { Dispatch, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Badge from '../../components/shared/Badge/Badge';
import { TemplateSummary } from '../../services/service-evaluations';
import { TAG_CHIP_STYLE } from '../../util/evaluationStatusMeta';
import QuestionPicker from '../app-evaluation-start/QuestionPicker';
import TemplateQuestions from './TemplateQuestions';
import {
  WizardAction,
  WizardData,
  WizardState,
  addedQuestionKey,
  extraRoom,
  previewQuestions,
  templateOf,
} from './wizardState';
import styles from './app-evaluation-wizard.module.scss';

export type TemplatesStatus = 'loading' | 'loaded' | 'failed';

type Props = {
  state: WizardState;
  data: WizardData;
  templatesStatus: TemplatesStatus;
  dispatch: Dispatch<WizardAction>;
};

const OWN_CARD = 'own';

const overlap = (template: TemplateSummary, tagIds: string[]) =>
  template.suggestedTagIds.filter(id => tagIds.includes(id)).length;

const TemplateStep = ({ state, data, templatesStatus, dispatch }: Props) => {
  const [t] = useTranslation('evaluations');
  const ownTagIds = state.tagsTouched ? state.tagIds : [];
  // Fixed once the templates are here: a choice rewrites the prefilled tags and must not move the cards
  const order = useRef<string[] | null>(null);
  if (!order.current && templatesStatus === 'loaded') {
    order.current = [...data.templates]
      .sort((a, b) => overlap(b, ownTagIds) - overlap(a, ownTagIds))
      .map(template => template._id);
  }
  const templates = (order.current ?? [])
    .map(id => data.templates.find(template => template._id === id))
    .filter((template): template is TemplateSummary => Boolean(template));
  const chosen = templateOf(state, data);
  const ownChosen = state.templateChoice?.kind === 'own';

  const tagNames = (ids: string[]) =>
    ids
      .map(id => data.tags.find(tag => tag._id === id && !tag.archived)?.name)
      .filter((name): name is string => Boolean(name));

  const templateCard = (template: TemplateSummary) => {
    const suggested = tagNames(template.suggestedTagIds);
    const selected = chosen?._id === template._id;
    return (
      <button
        key={template._id}
        type="button"
        className={classNames(styles.choice, { [styles.selected]: selected })}
        aria-pressed={selected}
        data-template-card={template._id}
        onClick={() =>
          dispatch({
            type: 'chooseTemplate',
            choice: { kind: 'template', templateId: template._id },
          })
        }
      >
        <span className={styles.choiceHead}>
          <span className={styles.choiceName}>{template.name}</span>
          <span className={styles.choiceCount}>
            {t('wizard.template.count', {
              count: template.questionCount,
              defaultValue_one: '{{count}} fråga',
              defaultValue_other: '{{count}} frågor',
            })}
          </span>
        </span>
        {template.description && <span className={styles.muted}>{template.description}</span>}
        {suggested.length > 0 && (
          <span className={`${styles.small} ${styles.muted}`}>
            {t('wizard.template.suggestedTags', 'Föreslagna taggar: {{names}}', {
              names: suggested.join(', '),
            })}
          </span>
        )}
        {overlap(template, ownTagIds) > 0 && (
          <span className={styles.badgeRow}>
            <Badge
              color={TAG_CHIP_STYLE.color}
              backgroundColor={TAG_CHIP_STYLE.backgroundColor}
              className={styles.badge}
            >
              {t('wizard.template.matchesTags', 'Föreslagen för valda taggar')}
            </Badge>
          </span>
        )}
      </button>
    );
  };

  return (
    <div className={styles.fields}>
      {templatesStatus === 'loading' && (
        <p className={styles.hint}>{t('wizard.template.loading', 'Hämtar mallar…')}</p>
      )}
      {templatesStatus === 'failed' && (
        <p className={styles.loadError}>
          {t('wizard.template.loadFailed', 'Mallarna kunde inte hämtas.')}
        </p>
      )}
      <div className={styles.choices}>
        {templates.map(templateCard)}
        <button
          type="button"
          className={classNames(styles.choice, { [styles.selected]: ownChosen })}
          aria-pressed={ownChosen}
          data-template-card={OWN_CARD}
          onClick={() => dispatch({ type: 'chooseTemplate', choice: { kind: 'own' } })}
        >
          <span className={styles.choiceHead}>
            <span className={styles.choiceName}>
              {t('wizard.template.own.name', 'Egna frågor')}
            </span>
            <span className={styles.choiceCount}>
              {t('wizard.template.own.count', 'Ingen mall')}
            </span>
          </span>
          <span className={styles.muted}>
            {t(
              'wizard.template.own.description',
              'Välj frågor ur organisationens frågelista för den här utvärderingen. Sparas inte som mall.',
            )}
          </span>
        </button>
      </div>
      {chosen && (
        <div className={styles.fields}>
          <div className={styles.fieldStack}>
            <p className={styles.sectionLabel}>
              {t('wizard.template.questionsLabel', 'Frågor i mallen')}
            </p>
            <TemplateQuestions
              questions={previewQuestions(chosen, state)}
              onToggleRemoved={key =>
                dispatch(
                  key.startsWith(addedQuestionKey(''))
                    ? {
                        type: 'setExtras',
                        extras: state.extraQuestions.filter(
                          extra => addedQuestionKey(extra._id) !== key,
                        ),
                      }
                    : { type: 'toggleRemoved', key },
                )
              }
              onSetRequired={(key, required) => dispatch({ type: 'setRequired', key, required })}
              onSetAllowSkip={(key, allowSkip) =>
                dispatch({ type: 'setAllowSkip', key, allowSkip })
              }
            />
          </div>
          <div className={styles.fieldStack}>
            <p className={styles.sectionLabel}>
              {t('wizard.template.addLabel', 'Lägg till egna frågor')}
            </p>
            <QuestionPicker
              mode="extra"
              max={extraRoom(chosen, state.removedQuestionKeys)}
              value={state.extraQuestions.map(item => item._id)}
              labels={Object.fromEntries(state.extraQuestions.map(item => [item._id, item.text]))}
              onChange={(_, picked) =>
                dispatch({
                  type: 'setExtras',
                  extras: picked.map(item => ({ _id: item.value, text: item.label })),
                })
              }
            />
            <p className={styles.hint}>
              {t(
                'wizard.template.addHint',
                'Tillagda frågor gäller bara den här utvärderingen. Mallen ändras inte.',
              )}
            </p>
          </div>
        </div>
      )}
      {ownChosen && (
        <QuestionPicker
          value={state.ownQuestionIds}
          onChange={ids => dispatch({ type: 'setOwnQuestions', ids })}
        />
      )}
    </div>
  );
};

export default TemplateStep;
